import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { Form, Message } from 'semantic-ui-react'
import { LoaderBar, PageHeader } from 'components'
import {
    AppButton,
    AppPrimaryButton,
    FormCheckBoxField,
    FormDateField,
    FormDropdownField,
    FormTextFormField,
    SubmitCancelButtons,
} from 'elements'
import { GetAuditorSummary } from 'hooks/auditor-summary'
import { GetCustomerList } from 'hooks'
import { DesktopMode, MobileMode } from 'layout'
import {
    APIPath,
    DatePickerTypes,
    buildSignInUrlWithReturn,
} from 'utility'
import { BaseService } from 'utility/services/base'
import { useAuth } from 'context'
import {
    AUDITOR_SUMMARY_DEFAULT_TARGET,
    AUDITOR_SUMMARY_PATH,
    AUDITOR_SUMMARY_TEMPLATE_ID,
    canViewAuditorSummary,
} from './constants'
import { getCopy } from './i18n'
import { Page } from './styles'
import {
    averageNumbers,
    clampTargetPct,
    formatExcelExpiry,
    formatGeneratedAt,
    formatPct,
    formatRangeLabel,
    formatYmd,
    getPctTone,
    readField,
    sumByIndex,
    sumTotals,
    toIdArray,
} from './utils'

const kpiToneClass = tone => {
    if (tone === 'ok') return 'pos'
    if (tone === 'amber') return 'warn'
    if (tone === 'bad') return 'neg'
    return ''
}

const getDefaultFromDate = () => new Date(new Date().getFullYear(), 0, 1)
const getDefaultToDate = () => new Date()

const PctPill = ({ value, targetPct }) => {
    const tone = getPctTone(value, targetPct)
    if (tone === 'blank') return <span className="pill blank">—</span>
    return <span className={`pill ${tone}`}>{formatPct(value)}</span>
}

export const AuditorSummaryScreen = () => {
    const navigate = useNavigate()
    const { user } = useAuth()
    const [lang, setLang] = useState('es')
    const t = getCopy(lang)
    const { data, error, errorStatus, loading, filter, filterChanged } =
        GetAuditorSummary()
    const { options: branchOptions } = GetCustomerList({ PageSize: 5000000 })
    const [auditorOptions, setAuditorOptions] = useState([])
    const [activeNav, setActiveNav] = useState('chart')

    const averageOptions = [
        { key: 'simple', value: 'simple', text: t.simpleMean },
        { key: 'weighted', value: 'weighted', text: t.weighted },
    ]

    useEffect(() => {
        let mounted = true
        const fetchTeamMembers = async () => {
            try {
                const response = await BaseService.get(APIPath.teamMember)
                const list = Array.isArray(response)
                    ? response
                    : response?.Items || []
                if (!mounted) return
                setAuditorOptions(
                    list.map(member => ({
                        key: member.unique_id,
                        text:
                            member.Name ||
                            member.Email ||
                            String(member.unique_id),
                        value: member.unique_id,
                    })),
                )
            } catch {
                if (mounted) setAuditorOptions([])
            }
        }
        fetchTeamMembers()
        return () => {
            mounted = false
        }
    }, [])

    const defaultFormValues = {
        FromDate: getDefaultFromDate(),
        ToDate: getDefaultToDate(),
        BranchIds: [],
        AuditorIds: [],
        TargetPct: AUDITOR_SUMMARY_DEFAULT_TARGET,
        AverageMode: 'simple',
        IncludeExcel: true,
    }

    const {
        control,
        handleSubmit,
        reset,
        watch,
        formState: { errors },
    } = useForm({
        defaultValues: defaultFormValues,
    })

    const fromDateValue = watch('FromDate')
    const appliedTarget = clampTargetPct(
        readField(filter, 'TargetPct', 'targetPct') ??
            AUDITOR_SUMMARY_DEFAULT_TARGET,
    )
    const appliedAverageMode =
        readField(filter, 'AverageMode', 'averageMode') || 'simple'
    const includeExcelRequested = Boolean(
        readField(filter, 'IncludeExcel', 'includeExcel'),
    )

    useEffect(() => {
        if (user && !canViewAuditorSummary(user)) {
            navigate('/app/templates', { replace: true })
        }
    }, [user, navigate])

    useEffect(() => {
        if (errorStatus === 401) {
            navigate(buildSignInUrlWithReturn(AUDITOR_SUMMARY_PATH))
        }
    }, [errorStatus, navigate])

    const kpis = data?.Kpis || data?.kpis || {}
    const months = data?.Months || data?.months || []
    const monthlyTrend = data?.MonthlyTrend || data?.monthlyTrend || []
    const byAuditor = data?.ByAuditor || data?.byAuditor || []
    const byBranch = data?.ByBranch || data?.byBranch || []
    const findingsByMonth = data?.FindingsByMonth || data?.findingsByMonth || []
    const findingsByAuditor =
        data?.FindingsByAuditor || data?.findingsByAuditor || []
    const reconciliation = data?.Reconciliation || data?.reconciliation || null
    const excelUrl = readField(data, 'ExcelUrl', 'excelUrl')
    const excelFileName = readField(data, 'ExcelFileName', 'excelFileName')
    const excelExpiresAt = readField(data, 'ExcelExpiresAt', 'excelExpiresAt')
    const generatedAt = readField(data, 'GeneratedAt', 'generatedAt')

    const auditsIncluded = Number(
        readField(kpis, 'AuditsIncluded', 'auditsIncluded') || 0,
    )
    const auditsExcluded = Number(
        readField(kpis, 'AuditsExcluded', 'auditsExcluded') || 0,
    )
    const overallAvgPct = readField(kpis, 'OverallAvgPct', 'overallAvgPct')
    const totalFindings = Number(
        readField(kpis, 'TotalFindings', 'totalFindings') || 0,
    )
    const worstMonthLabel = readField(kpis, 'WorstMonthLabel', 'worstMonthLabel')
    const worstMonthCount = readField(kpis, 'WorstMonthCount', 'worstMonthCount')
    const branchesActive = Number(
        readField(kpis, 'BranchesActive', 'branchesActive') || 0,
    )
    const auditorsActive = Number(
        readField(kpis, 'AuditorsActive', 'auditorsActive') || 0,
    )

    const isEmpty = Boolean(data) && auditsIncluded === 0
    const hasReport = Boolean(data) && !isEmpty
    const excelFailed = Boolean(data) && includeExcelRequested && !excelUrl
    const overallTone = getPctTone(overallAvgPct, appliedTarget)
    const branchOverall = averageNumbers(
        byBranch.map(row => readField(row, 'AverageOverall', 'averageOverall')),
    )
    const modeLabel =
        appliedAverageMode === 'weighted' ? t.weightedMode : t.simpleMode
    const amberFloor = Math.max(0, appliedTarget - 10)
    const reconOk = readField(reconciliation, 'Ok', 'ok') !== false
    let worstMonthText = '—'
    if (worstMonthLabel && worstMonthCount == null) {
        worstMonthText = worstMonthLabel
    } else if (worstMonthLabel) {
        worstMonthText = `${worstMonthLabel} (${worstMonthCount})`
    }

    const submit = formData => {
        filterChanged({
            TemplateId: AUDITOR_SUMMARY_TEMPLATE_ID,
            FromDate: formatYmd(formData.FromDate),
            ToDate: formatYmd(formData.ToDate),
            BranchIds: toIdArray(formData.BranchIds),
            AuditorIds: toIdArray(formData.AuditorIds),
            TargetPct: clampTargetPct(formData.TargetPct),
            AverageMode: formData.AverageMode || 'simple',
            IncludeExcel: formData.IncludeExcel === true,
        })
    }

    const resetForm = () => {
        reset(defaultFormValues)
    }

    const scrollTo = id => {
        setActiveNav(id)
        const el = document.getElementById(id)
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    const navItems = [
        { id: 'chart', label: t.navTrend },
        { id: 's1', label: t.navAuditor },
        { id: 's2', label: t.navBranch },
        { id: 's3', label: t.navFindingsMonth },
        { id: 's4', label: t.navFindingsAuditor },
    ]

    const LangPrintToolbar = (
        <div className="toolbar no-print">
            <div className="lang-toggle">
                <button
                    type="button"
                    className={lang === 'es' ? 'active' : ''}
                    onClick={() => setLang('es')}
                >
                    ES
                </button>
                <button
                    type="button"
                    className={lang === 'en' ? 'active' : ''}
                    onClick={() => setLang('en')}
                >
                    EN
                </button>
            </div>
            <AppButton type="button" onClick={() => window.print()}>
                {t.print}
            </AppButton>
        </div>
    )

    return (
        <Page>
            {loading && <LoaderBar content={t.loading} />}
            <div className="page-head">
                <div>
                    <PageHeader header={t.title} />
                    <p className="page-sub">
                        <b>{t.product}</b>
                        {' · '}
                        {formatRangeLabel(
                            filter?.FromDate,
                            filter?.ToDate,
                            months,
                        ) || t.allHistory}
                    </p>
                </div>
                <DesktopMode>{LangPrintToolbar}</DesktopMode>
            </div>
            <MobileMode>
                <div className="mb-3">{LangPrintToolbar}</div>
            </MobileMode>

            <Form noValidate>
                <Form.Group widths="equal">
                    <FormDateField
                        name="FromDate"
                        control={control}
                        label={t.from}
                        errors={errors.FromDate}
                        type={DatePickerTypes.Single}
                    />
                    <FormDateField
                        name="ToDate"
                        control={control}
                        label={t.to}
                        disabledBefore={fromDateValue}
                        errors={errors.ToDate}
                        type={DatePickerTypes.Single}
                    />
                </Form.Group>
                <Form.Group widths="equal">
                    <FormDropdownField
                        control={control}
                        name="BranchIds"
                        label={t.branches}
                        placeholder={t.allBranches}
                        options={branchOptions}
                        defaultValue={[]}
                        multiple
                        clearable
                    />
                    <FormDropdownField
                        control={control}
                        name="AuditorIds"
                        label={t.auditors}
                        placeholder={t.allAuditors}
                        options={auditorOptions}
                        defaultValue={[]}
                        multiple
                        clearable
                    />
                </Form.Group>
                <Form.Group widths="equal">
                    <FormTextFormField
                        control={control}
                        name="TargetPct"
                        label={`${t.target} (%)`}
                        type="number"
                        min={0}
                        max={100}
                    />
                    <FormDropdownField
                        control={control}
                        name="AverageMode"
                        label={t.average}
                        options={averageOptions}
                    />
                </Form.Group>
                <Form.Field className="excel-check mb-2">
                    <FormCheckBoxField
                        control={control}
                        name="IncludeExcel"
                        label={t.excelToggleLead}
                    />
                    <p className="excel-hint">{t.excelToggleRest}</p>
                </Form.Field>
                <div className="pt-1">
                    <SubmitCancelButtons
                        loading={loading}
                        onSubmit={handleSubmit(submit)}
                        onCancel={resetForm}
                        submitText={t.apply}
                        cancelText={t.cancel}
                    />
                </div>
            </Form>

            <div className="kpis">
                <div className="kpi">
                    <div className="lbl">{t.auditsIncluded}</div>
                    <div className="val">{auditsIncluded}</div>
                    <div className="hint">{t.excludedHint(auditsExcluded)}</div>
                </div>
                <div className="kpi">
                    <div className="lbl">{t.overallAvg}</div>
                    <div className={`val ${kpiToneClass(overallTone)}`}>
                        {formatPct(overallAvgPct) || '—'}
                    </div>
                    <div className="hint">{t.targetHint(appliedTarget)}</div>
                </div>
                <div className="kpi">
                    <div className="lbl">{t.totalFindings}</div>
                    <div className={`val ${totalFindings > 0 ? 'warn' : ''}`}>
                        {totalFindings}
                    </div>
                    <div className="hint">
                        {t.worstMonth}: {worstMonthText}
                    </div>
                </div>
                <div className="kpi">
                    <div className="lbl">{t.branchesAuditors}</div>
                    <div className="val">
                        {branchesActive} · {auditorsActive}
                    </div>
                    <div className="hint">{t.activeInRange}</div>
                </div>
            </div>

            {error && errorStatus !== 401 && (
                <Message negative>
                    <Message.Header>{error || t.errorGeneric}</Message.Header>
                </Message>
            )}

            {excelUrl && (
                <Message positive>
                    <Message.Header>{t.excelGenerated}</Message.Header>
                    <p>
                        {excelFileName ? <code>{excelFileName}</code> : null}
                        {excelExpiresAt
                            ? ` · ${formatExcelExpiry(excelExpiresAt)}`
                            : ''}
                    </p>
                    <div className="excel-actions">
                        <AppPrimaryButton
                            type="button"
                            onClick={() => window.open(excelUrl)}
                        >
                            {t.downloadExcel}
                        </AppPrimaryButton>
                    </div>
                    <p className="excel-hint">{t.excelShareHint}</p>
                </Message>
            )}

            {excelFailed && (
                <Message warning>
                    <p>{t.excelFailed}</p>
                </Message>
            )}

            {isEmpty && <Message info>{t.empty}</Message>}

            {hasReport && (
                <div className="results">
                    <div className="nav">
                        {navItems.map(item => (
                            <button
                                key={item.id}
                                type="button"
                                className={activeNav === item.id ? 'active' : ''}
                                onClick={() => scrollTo(item.id)}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>

                        <div className="chart-card" id="chart">
                            <h3>{t.chartTitle}</h3>
                            <div className="desc">
                                {t.chartDesc(modeLabel, appliedTarget)}
                            </div>
                            <div className="chart">
                                {monthlyTrend.map((point, index) => {
                                    const label =
                                        point.Label ||
                                        point.label ||
                                        months[index]?.Label ||
                                        `M${index + 1}`
                                    const avg = readField(point, 'AvgPct', 'avgPct')
                                    const tone = getPctTone(avg, appliedTarget)
                                    const width = Math.max(
                                        0,
                                        Math.min(100, Number(avg) || 0),
                                    )
                                    return (
                                        <ChartRow
                                            key={`${label}-${index}`}
                                            label={label}
                                            width={width}
                                            tone={tone}
                                            value={formatPct(avg) || '—'}
                                            target={appliedTarget}
                                        />
                                    )
                                })}
                            </div>
                        </div>

                        <h2 className="as-h2" id="s1">
                            <span className="num">1</span>
                            {t.table1}
                            <span className="info">
                                {t.auditorsMeta(byAuditor.length, modeLabel)}
                            </span>
                        </h2>
                        <PercentTable
                            firstHeader={t.auditor}
                            months={months}
                            averageCol={t.averageCol}
                            rows={byAuditor}
                            nameKey={['AuditorName', 'auditorName']}
                            idKey={['AuditorUserId', 'auditorUserId']}
                            overallAvgPct={overallAvgPct}
                            overallLabel={t.overallAverage}
                            targetPct={appliedTarget}
                        />

                        <h2 className="as-h2" id="s2">
                            <span className="num">2</span>
                            {t.table2}
                            <span className="info">
                                {t.branchesMeta(byBranch.length, modeLabel)}
                            </span>
                        </h2>
                        <PercentTable
                            firstHeader={t.branch}
                            months={months}
                            averageCol={t.averageCol}
                            rows={byBranch}
                            nameKey={['BranchName', 'branchName']}
                            idKey={['BranchId', 'branchId']}
                            overallAvgPct={branchOverall}
                            overallLabel={t.overallAverage}
                            targetPct={appliedTarget}
                        />

                        <h2 className="as-h2" id="s3">
                            <span className="num">3</span>
                            {t.table3}
                            <span className="info">{t.findingsMeta(totalFindings)}</span>
                        </h2>
                        <CountTable
                            firstHeader={t.process}
                            headers={months.map(m => m.Label || m.label)}
                            rows={findingsByMonth}
                            getName={row => readField(row, 'Process', 'process')}
                            getCells={row =>
                                readField(row, 'Monthly', 'monthly') || []
                            }
                            getTotal={row => readField(row, 'Total', 'total')}
                            totalLabel={t.total}
                        />

                        <h2 className="as-h2" id="s4">
                            <span className="num">4</span>
                            {t.table4}
                            <span className="info">{t.table4Info}</span>
                        </h2>
                        <CountTable
                            firstHeader={t.process}
                            headers={byAuditor.map(row =>
                                readField(row, 'AuditorName', 'auditorName'),
                            )}
                            rows={findingsByAuditor}
                            getName={row => readField(row, 'Process', 'process')}
                            getCells={row =>
                                readField(row, 'PerAuditor', 'perAuditor') || []
                            }
                            getTotal={row => readField(row, 'Total', 'total')}
                            totalLabel={t.total}
                        />

                        {reconciliation && (
                            <div className={`recon ${reconOk ? '' : 'fail'}`}>
                                <div className="check">{reconOk ? '✓' : '!'}</div>
                                <div>
                                    <b>{t.recon}</b>
                                    {': '}
                                    {readField(reconciliation, 'Note', 'note')}
                                </div>
                            </div>
                        )}

                        <div className="legend">
                            <div className="item">
                                <span className="sw" style={{ background: '#dcfce7' }} />
                                {t.legendOk(appliedTarget)}
                            </div>
                            <div className="item">
                                <span className="sw" style={{ background: '#fef3c7' }} />
                                {t.legendAmber(
                                    amberFloor,
                                    Math.max(0, appliedTarget - 1),
                                )}
                            </div>
                            <div className="item">
                                <span className="sw" style={{ background: '#fee2e2' }} />
                                {t.legendBad(amberFloor)}
                            </div>
                            <div className="item">
                                <span
                                    className="sw"
                                    style={{
                                        background:
                                            'repeating-linear-gradient(45deg,#fafafa,#fafafa 4px,#f3f4f6 4px,#f3f4f6 8px)',
                                    }}
                                />
                                {t.legendBlank}
                            </div>
                        </div>
                    </div>
                )}

            <Message info>
                <Message.Header>{t.excelHowTitle}</Message.Header>
                <p>{t.excelHowBody}</p>
                <p className="excel-hint">{t.excelHowHint}</p>
            </Message>

            <div className="footer-meta">
                <div>
                    <b>{t.generated}</b>: {formatGeneratedAt(generatedAt)}
                </div>
                <div>
                    {t.applied}: {branchesActive} {t.branches.toLowerCase()} ·{' '}
                    {auditorsActive} {t.auditors.toLowerCase()} · {t.target}{' '}
                    <b>{appliedTarget}%</b> · {auditsIncluded} {t.audits}
                </div>
            </div>
        </Page>
    )
}

const ChartRow = ({ label, width, tone, value, target }) => (
    <>
        <div className="lbl">{label}</div>
        <div className="track">
            <div
                className={`bar ${tone === 'blank' ? '' : tone}`}
                style={{ width: `${width}%` }}
            />
            <div
                className="target"
                style={{ left: `${Math.min(100, Math.max(0, target))}%` }}
            />
        </div>
        <div className="val">{value}</div>
    </>
)

const PercentTable = ({
    firstHeader,
    months,
    averageCol,
    rows,
    nameKey,
    idKey,
    overallAvgPct,
    overallLabel,
    targetPct,
}) => (
    <div className="tbl-wrap">
        <table className="as-table">
            <thead>
                <tr>
                    <th>{firstHeader}</th>
                    {months.map((month, index) => (
                        <th key={index}>{month.Label || month.label}</th>
                    ))}
                    <th>{averageCol}</th>
                </tr>
            </thead>
            <tbody>
                {rows.map(row => {
                    const monthly = readField(row, 'Monthly', 'monthly') || []
                    return (
                        <tr
                            key={
                                readField(row, idKey[0], idKey[1]) ||
                                readField(row, nameKey[0], nameKey[1])
                            }
                        >
                            <td>{readField(row, nameKey[0], nameKey[1])}</td>
                            {months.map((_, index) => (
                                <td key={index}>
                                    <PctPill
                                        value={monthly[index]}
                                        targetPct={targetPct}
                                    />
                                </td>
                            ))}
                            <td>
                                <PctPill
                                    value={readField(
                                        row,
                                        'AverageOverall',
                                        'averageOverall',
                                    )}
                                    targetPct={targetPct}
                                />
                            </td>
                        </tr>
                    )
                })}
                <tr className="promedio">
                    <td colSpan={months.length + 1}>{overallLabel}</td>
                    <td>{formatPct(overallAvgPct) || '—'}</td>
                </tr>
            </tbody>
        </table>
    </div>
)

const CountTable = ({
    firstHeader,
    headers,
    rows,
    getName,
    getCells,
    getTotal,
    totalLabel,
}) => (
    <div className="tbl-wrap">
        <table className="as-table">
            <thead>
                <tr>
                    <th>{firstHeader}</th>
                    {headers.map((header, index) => (
                        <th key={index}>{header}</th>
                    ))}
                    <th>{totalLabel}</th>
                </tr>
            </thead>
            <tbody>
                {rows.map(row => {
                    const cells = getCells(row) || []
                    return (
                        <tr key={getName(row)}>
                            <td>{getName(row)}</td>
                            {headers.map((_, index) => (
                                <td key={index}>{cells[index] ?? 0}</td>
                            ))}
                            <td>
                                <strong>{getTotal(row) ?? 0}</strong>
                            </td>
                        </tr>
                    )
                })}
                <tr className="total">
                    <td>{totalLabel}</td>
                    {headers.map((_, index) => (
                        <td key={index}>
                            {sumByIndex(rows, getCells, index)}
                        </td>
                    ))}
                    <td>{sumTotals(rows, getTotal)}</td>
                </tr>
            </tbody>
        </table>
    </div>
)

export default AuditorSummaryScreen
